import * as React from 'react'
import { useCallback, useEffect } from 'react'
import { Theme } from '@mui/material/styles'
import {
  Field,
  FieldArray,
  FieldArrayRenderProps,
  getIn,
  useFormikContext,
} from 'formik'

import { DebouncedTextField } from '@user/components/Formik'
import { IEmailCollectionItem } from '@sales-center/common'
import { TextFieldProps } from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'

interface Props {
  name: string
  required: boolean
  singleModeEnabled?: boolean
  textFieldProps?: TextFieldProps
  spacing?: number
  paddingTop?: number
  radioPaddingTop?: number
  avoidValidateOnBlur?: boolean
  removeIcon?: React.ReactNode
  onChangeHandler?: () => void
}

export const EmailCollection = <T,>(props: Props) => {
  const {
    name,
    required,
    singleModeEnabled = true,
    avoidValidateOnBlur = false,
    textFieldProps,
    spacing = 1,
    paddingTop = 16,
    removeIcon,
    onChangeHandler,
  } = props

  const { setFieldValue, values } = useFormikContext<T>()
  const emailsAddresses: IEmailCollectionItem[] = getIn(values, name)

  let boundArrayHelpers
  const bindArrayHelpers = (helpers: FieldArrayRenderProps) => {
    boundArrayHelpers = helpers
  }

  const showRemoveButton = emailsAddresses && emailsAddresses.length > 1
  const singleEmailMode =
    singleModeEnabled && emailsAddresses && emailsAddresses.length === 1

  const onChangeRadio = useCallback(
    (event, _checked) => {
      const selectedRadioIndex: number = Number(event.target.value)
      const emailsUpdate: IEmailCollectionItem[] = []
      emailsAddresses.map((value: IEmailCollectionItem, index) =>
        emailsUpdate.push({
          emailAddress: value.emailAddress,
          isPrimary: index === selectedRadioIndex,
        }),
      )
      setFieldValue(name, emailsUpdate)
    },
    [name, setFieldValue, emailsAddresses],
  )

  const onRemoveClick = useCallback(
    (index: number) => {
      if (emailsAddresses[index].isPrimary) {
        const newPrimaryIndex = index === 0 ? 1 : Math.max(0, index - 1)
        setFieldValue('primaryEmailIndex', newPrimaryIndex)
        setFieldValue(`${name}[${newPrimaryIndex}].isPrimary`, true)
      }
      boundArrayHelpers.remove(index)
    },
    [boundArrayHelpers, name, setFieldValue, emailsAddresses],
  )

  const handleFieldChange = useCallback(
    (_) => {
      if (onChangeHandler) {
        onChangeHandler()
      }
    },
    [onChangeHandler],
  )

  useEffect(() => {
    const emails: IEmailCollectionItem[] = getIn(values, name)

    if (!emails || emails.length === 0) {
      setFieldValue(`${name}`, [{ emailAddress: '', isPrimary: true }])
    }
  }, [getIn, name, setFieldValue, values])

  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => {
        bindArrayHelpers(arrayHelpers)
        return (
          <>
            {emailsAddresses &&
              emailsAddresses.length > 0 &&
              emailsAddresses.map((_item: IEmailCollectionItem, index) => (
                <React.Fragment key={index}>
                  <Grid
                    container
                    item
                    xs={12}
                    sx={{
                      paddingBottom: (theme: Theme) =>
                        singleEmailMode ? theme.spacing(0.5) : 0,
                      paddingTop: (theme: Theme) =>
                        theme.spacing(index > 0 ? spacing : paddingTop / 10),
                    }}
                  >
                    <Field
                      name={`${name}[${index}].emailAddress`}
                      label="Email Address"
                      component={DebouncedTextField}
                      required={required && emailsAddresses[index].isPrimary}
                      {...textFieldProps}
                      inputProps={{ maxLength: 200 }}
                      onChange={handleFieldChange}
                      avoidValidateOnBlur={avoidValidateOnBlur}
                      data-test-id={`${name}-${index}-emailAddress`}
                    />
                  </Grid>

                  {!singleEmailMode && (
                    <Grid
                      container
                      item
                      xs={12}
                      sx={{
                        paddingTop: (theme: Theme) => theme.spacing(1),
                        paddingBottom:
                          index === emailsAddresses.length - 1 ? 1 : 2,
                      }}
                    >
                      <Grid item xs={9}>
                        <FormControlLabel
                          label="Primary Email Address"
                          name="primaryEmailIndex"
                          value={index}
                          control={<Radio />}
                          sx={{ margin: (theme: Theme) => theme.spacing(0) }}
                          checked={emailsAddresses[index].isPrimary}
                          onChange={onChangeRadio}
                          data-test-id={`${name}-${index}-primaryEmail`}
                        />
                      </Grid>
                      {showRemoveButton && (
                        <Grid item xs style={{ textAlign: 'right' }}>
                          <IconButton
                            onClick={() => onRemoveClick(index)}
                            size="medium"
                            data-test-id={`${name}-${index}-removeEmail`}
                          >
                            {removeIcon || (
                              <DeleteIcon sx={{ color: '#AB1010' }} />
                            )}
                          </IconButton>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </React.Fragment>
              ))}
          </>
        )
      }}
    />
  )
}
